import Storage from 'utils/localStorage/Storage';
import userUtils from 'utils/User';
import { loadBeyondIdentityUtils } from 'utils/lazyPasskey';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import { MAX_TOTAL_DISMISSALS, FOURTEEN_DAYS_MS } from 'constants/passkeys';
import passkeysActions from 'actions/PasskeysActions';
import PasskeyService from 'services/PasskeyService';
import UI from 'utils/UI';
import { classifyPasskeyError, logPasskeyForDynatrace } from 'utils/passkeyDynatrace';

const isPasskeyEnabled = () => Sephora.configurationSettings?.passkey?.isPasskeyEnabled;

const isFirefoxAndroid = () => UI.isFirefox() && UI.isAndroid();

const getTrackingData = () => {
    return Storage.local.getItem(LOCAL_STORAGE.PASSKEY_OPTIN_TRACKING) || {};
};

let cachedPasskeySupported = null;

const isPasskeySupported = async () => {
    if (cachedPasskeySupported !== null) {
        return cachedPasskeySupported;
    }

    if (typeof window === 'undefined' || !window.PublicKeyCredential) {
        cachedPasskeySupported = false;

        return false;
    }

    if (isFirefoxAndroid()) {
        cachedPasskeySupported = false;

        return false;
    }

    try {
        cachedPasskeySupported = !!(await window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable());

        return cachedPasskeySupported;
    } catch (error) {
        cachedPasskeySupported = false;
        logPasskeyForDynatrace('WebAuthn', 'SupportCheck', error, 'support-check');

        return false;
    }
};

const getDevicePasskeys = async () => {
    try {
        const BeyondIdentityUtils = await loadBeyondIdentityUtils();
        const currentEmail = userUtils.getProfileEmail();
        const devicePasskeys = await BeyondIdentityUtils.getPasskeys();
        const devicePasskey = devicePasskeys.find(passkey => passkey?.identity?.emailAddress === currentEmail);

        return devicePasskey;
    } catch (error) {
        logPasskeyForDynatrace(classifyPasskeyError(error), 'GetPasskeys', error, 'device-check');

        return null;
    }
};

const doesDevicePasskeyExistInAllPasskeys = (allPasskeys, devicePasskey) => {
    try {
        const existsInAllPasskeys = allPasskeys.some(
            passkey => passkey.passkeyId === devicePasskey.passkeyId && passkey.identityId === devicePasskey.identity.id
        );

        return existsInAllPasskeys;
    } catch (error) {
        logPasskeyForDynatrace(classifyPasskeyError(error), 'CheckPasskey', error, 'device-check');

        return false;
    }
};

const fetchPasskeysQuietly = async () => {
    try {
        const resp = await PasskeyService.getPasskeys();

        return Object.values(resp).filter(item => item.passkeyId);
    } catch (err) {
        logPasskeyForDynatrace(classifyPasskeyError(err), 'GetPasskeys', err, 'silent-fetch');

        return null;
    }
};

const isPasskeyExistsInCurrentDevice = async (hasIdentity = false, passkeys = [], isLoadedPasskeys = false, silent = false) => {
    try {
        if (!isLoadedPasskeys && userUtils.isAnonymous()) {
            return false;
        }

        const devicePasskey = isPasskeyEnabled() ? await getDevicePasskeys() : null;

        // Fetch passkeys from backend when the user has a passkey identity or a device
        // passkey exists. Using OR covers two cases:
        // - hasIdentity true, no devicePasskey: populates Redux for AccountPasskeys
        //   when viewing from a different browser (cross-browser display fix).
        // - devicePasskey exists, hasIdentity false: prevents wrongful deletion of
        //   valid device passkeys after password reset (ACS-3796 fix).
        const allPasskeys = isLoadedPasskeys
            ? passkeys
            : devicePasskey || hasIdentity
                ? silent
                    ? await fetchPasskeysQuietly()
                    : await passkeysActions.getPasskeysData()
                : [];

        if (!devicePasskey) {
            return false;
        }

        if (allPasskeys === null) {
            return false;
        }

        const existsInAllPasskeys = doesDevicePasskeyExistInAllPasskeys(allPasskeys, devicePasskey);

        if (!existsInAllPasskeys) {
            if (isPasskeyEnabled()) {
                const BeyondIdentityUtils = await loadBeyondIdentityUtils();
                BeyondIdentityUtils.deletePasskey(devicePasskey.id);
            }
        }

        return existsInAllPasskeys ? devicePasskey.passkeyId : false;
    } catch (error) {
        logPasskeyForDynatrace(classifyPasskeyError(error), 'ExistenceCheck', error, 'optin-check');

        return false;
    }
};

const isPasskeyDisabled = async isEnabledEntryFlag => {
    const isSupported = await isPasskeySupported();

    return !isPasskeyEnabled() || !isSupported || !isEnabledEntryFlag;
};

const resetTracking = () => {
    Storage.local.removeItem(LOCAL_STORAGE.PASSKEY_OPTIN_TRACKING);
    const dismissedCount = Storage.local.getItem(LOCAL_STORAGE.PASSKEY_OPTIN_DISMISSED) || {};
    const count = (dismissedCount.count || 0) + 1;
    Storage.local.setItem(LOCAL_STORAGE.PASSKEY_OPTIN_DISMISSED, { count });
};

const trackDismissal = () => {
    let trackingData = getTrackingData();

    // Initialize tracking if not exists
    if (!trackingData?.count) {
        trackingData = { count: 0, lastDismissed: null, pauseUntil: null };
    }

    // Increment count
    trackingData.count += 1;
    trackingData.lastDismissed = new Date().getTime();

    if (trackingData.count >= MAX_TOTAL_DISMISSALS) {
        // Set 14-day pause
        trackingData.pauseUntil = new Date().getTime() + FOURTEEN_DAYS_MS;
    }

    Storage.local.setItem(LOCAL_STORAGE.PASSKEY_OPTIN_TRACKING, trackingData);
};

const cleanupPasskeysByEmail = async email => {
    if (!email || !isPasskeyEnabled()) {
        return;
    }

    try {
        const BeyondIdentityUtils = await loadBeyondIdentityUtils();
        const localPasskeys = await BeyondIdentityUtils.getPasskeys();

        if (!localPasskeys || localPasskeys.length === 0) {
            return;
        }

        const normalizedEmail = email.trim().toLowerCase();
        const deletePromises = localPasskeys
            .filter(pk => {
                const passkeyEmail = pk?.identity?.emailAddress || pk?.identity?.traits?.username;

                return passkeyEmail?.trim().toLowerCase() === normalizedEmail;
            })
            .map(async pk => {
                try {
                    await BeyondIdentityUtils.deletePasskey(pk.id);
                } catch (deleteError) {
                    Sephora.logger.error('[Passkey] Failed to delete device passkey', {
                        email,
                        passkeyId: pk.id,
                        error: deleteError?.message || deleteError
                    });
                    logPasskeyForDynatrace(classifyPasskeyError(deleteError), 'Delete', deleteError, 'cleanup');
                }
            });

        await Promise.all(deletePromises);
    } catch (error) {
        Sephora.logger.error('[Passkey] Failed to cleanup passkeys', {
            email,
            error: error?.message || error
        });
        logPasskeyForDynatrace(classifyPasskeyError(error), 'Cleanup', error, 'cleanup');
    }
};

export default {
    isPasskeySupported,
    isPasskeyDisabled,
    isFirefoxAndroid,
    resetTracking,
    getTrackingData,
    trackDismissal,
    isPasskeyExistsInCurrentDevice,
    isPasskeyEnabled,
    cleanupPasskeysByEmail
};
